'use client';

import { useEffect, useState } from 'react';

// Keep in sync with the `scroll-margin-top: 5rem` on headings
// (styles/Heading.module.scss): a heading scrolled to its anchor position
// must register as passed, so the offset needs a little slack beyond 5rem.
const SCROLL_SPY_OFFSET_PX = 88;

/**
 * Track which in-page section the user is looking at.
 *
 * Feed it the anchor ids of a page's section headings (in document order) and
 * highlight the returned id in an on-page navigation menu. The active id is
 * the last heading that scrolled past the offset line near the viewport top —
 * i.e. the section whose content is currently on screen. Before the first
 * heading it returns the first id; at the very bottom of the page, the last.
 *
 * Returns null until mounted, so server and first client render match.
 */
export function useScrollSpy(ids: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  // The callers build the ids array inline on each render; key the effect on
  // the contents rather than the array identity.
  const idsKey = ids.join('\n');

  useEffect(() => {
    const spyIds = idsKey.split('\n').filter(Boolean);
    if (spyIds.length === 0) {
      return;
    }

    let frame: number | null = null;
    // While a fragment navigation is smooth-scrolling to its target, hold the
    // target highlighted instead of flickering through the headings passed
    // on the way.
    let holdingFragment = false;
    let settleTimer: number | undefined;

    const compute = () => {
      frame = null;
      if (holdingFragment) {
        return;
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
        setActiveId(spyIds[spyIds.length - 1]);
        return;
      }
      let current = spyIds[0];
      for (const id of spyIds) {
        // getElementById, not querySelector: some anchor ids contain dots.
        const element = document.getElementById(id);
        if (element !== null && element.getBoundingClientRect().top <= SCROLL_SPY_OFFSET_PX) {
          current = id;
        }
      }
      setActiveId(current);
    };

    const schedule = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(compute);
      }
    };

    const releaseFragment = () => {
      window.clearTimeout(settleTimer);
      window.removeEventListener('scrollend', releaseFragment);
      holdingFragment = false;
      schedule();
    };

    const onScroll = () => {
      if (holdingFragment) {
        // No scrollend support: treat a quiet period as the scroll settling.
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(releaseFragment, 150);
      } else {
        schedule();
      }
    };

    const onHashChange = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!spyIds.includes(id)) {
        return;
      }
      setActiveId(id);
      holdingFragment = true;
      window.addEventListener('scrollend', releaseFragment, { once: true });
      // Covers the case where the target is already in position and no
      // scroll (and thus no scrollend) happens at all.
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(releaseFragment, 300);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('hashchange', onHashChange);

    if (window.location.hash) {
      onHashChange();
    } else {
      compute();
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('scrollend', releaseFragment);
      window.clearTimeout(settleTimer);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [idsKey]);

  return activeId;
}
